import { atom, useAtomValue } from 'jotai'
import { atomFamily, unwrap } from 'jotai/utils'
import { useTranslation } from 'react-i18next'
import { itemsFromTags, makeListFetcher } from '@nostr/gadgets/lists'
import { currentPubkeyAtom } from '@/atoms/session'
import { metadataChannelAtom } from '@/atoms/invalidation'

export type PaymentTarget = {
  type: string
  address: string
  uri: string
}

export const loadPaymentTargets = makeListFetcher(
  10133,
  window.hallway.universe.indexerUrls,
  itemsFromTags((tag: string[]): PaymentTarget | undefined => {
    if (tag[0] !== 'payto' || !tag[1] || !tag[2]) return

    const type = tag[1].toLowerCase()
    const address = tag[2]
    let uri: string
    if (['monero', 'bitcoin', 'lightning', 'zcash', 'nano'].includes(type)) {
      uri = `${type}:${address}`
    } else if (type === 'venmo') {
      uri = `https://venmo.com/${address}`
    } else if (type === 'cashme') {
      uri = `https://cash.me/${address}`
    } else if (type === 'paypal') {
      uri = `https://paypal.me/${address}`
    } else if (type.startsWith('bip-')) {
      uri = `bitcoin:${address}`
    } else {
      uri = `payto://${type}/${address}`
    }

    return { type, address, uri }
  })
)

const paymentTargetsAtom = atomFamily((pubkey: string) =>
  atom(async (get) => {
    get(metadataChannelAtom)
    if (!window.hallway.universe.enableNipA3) return []

    try {
      const { items } = await loadPaymentTargets(pubkey)
      return items
    } catch {
      return []
    }
  })
)

const paymentTargetsValueAtom = atomFamily((pubkey: string) => unwrap(paymentTargetsAtom(pubkey)))

export const currentPaymentTargetsAtom = atom((get) => {
  const pubkey = get(currentPubkeyAtom)
  return pubkey ? get(paymentTargetsAtom(pubkey)) : []
})

export default function PaymentTargets({ pubkey }: { pubkey: string }) {
  const { t } = useTranslation()
  const targets = useAtomValue(paymentTargetsValueAtom(pubkey)) ?? []

  if (!window.hallway.universe.enableNipA3 || targets.length === 0) return null

  return (
    <div className="pr-4 pt-2 flex flex-wrap gap-2">
      {targets.map((target, index) => (
        <a
          key={`${target.type}-${target.address}-${index}`}
          href={target.uri}
          title={target.address}
          target="_blank"
          rel="noreferrer"
          className="rounded-full bg-muted px-3 py-1 text-sm text-primary hover:underline"
        >
          {t(target.type)}
        </a>
      ))}
    </div>
  )
}
